import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { NodeGroupCreationConfig } from '../types';
import {
  getApiTimeoutMs,
  getAuthStorageRaw,
  getSessionItem,
  isDevelopmentEnv,
  removeSessionItem,
} from '../config/runtimeConfig';

const API_TIMEOUT = getApiTimeoutMs();

// Create axios instance. In dev, baseURL is /api so the browser shows localhost:3000/api/...;
// the dev proxy (setupProxy.js) forwards to REACT_APP_API_BASE_URL or x-custom-server.
const createApiInstance = (): AxiosInstance => {
  return axios.create({
    // Always call same-origin /api; dev uses setupProxy.js and Docker production uses Nginx /api reverse proxy.
    baseURL: '/api',
    timeout: API_TIMEOUT,
    withCredentials: false, // Don't send cookies
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

// Request interceptor to add auth token and custom server header for proxy
const createRequestInterceptor = (api: AxiosInstance) => {
  api.interceptors.request.use(
    (config) => {
      // Send Stream Manager URL so the dev proxy forwards to it (browser shows localhost:3000; proxy forwards to this URL)
      const customServerURL = getSessionItem('api-base-url');
      if (customServerURL && customServerURL.trim()) {
        config.headers['x-custom-server'] = customServerURL.trim().replace(/\/$/, '');
      }
      // Get token from Zustand store via sessionStorage
      const authStorage = getAuthStorageRaw();
      if (authStorage) {
        try {
          const authData = JSON.parse(authStorage);
          if (authData.state?.token) {
            config.headers.Authorization = `Bearer ${authData.state.token}`;
          }
        } catch (error) {
          if (isDevelopmentEnv()) {
            console.error('Error parsing auth storage:', error);
          }
        }
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
};

// Response interceptor for error handling
const createResponseInterceptor = (api: AxiosInstance) => {
  api.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error) => {
      if (isDevelopmentEnv() && error?.response?.status !== 401) {
        console.error('API Error:', error.response?.data ?? error.message);
      }
      if (error.response?.status === 401) {
        // Token expired or invalid
        removeSessionItem('auth-storage');
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );
};

// Create and configure API instance
const api = createApiInstance();
createRequestInterceptor(api);
createResponseInterceptor(api);

// Auth API
export const authAPI = {
  login: async (username: string, password: string) => {
    // Create Basic Auth header
    const credentials = btoa(`${username}:${password}`);
    
    const response = await api.put('/as/v1/auth/login', {}, {
      headers: {
        'Authorization': `Basic ${credentials}`,
        'accept': 'application/json'
      }
    });
    return response.data;
  },
};

// Node Group API - Updated to use the correct Stream Manager endpoints
export const nodeGroupAPI = {
  // Get all node groups - using the correct endpoint from the working curl command
  getNodeGroups: async () => {
    try {
      const response = await api.get('/as/v1/admin/nodegroup', {
        headers: {
          'accept': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      if (isDevelopmentEnv()) console.error('Error fetching node groups:', error);
      throw error;
    }
  },

  // Get node group status - returns detailed information about active nodes
  getNodeGroupStatus: async (nodeGroupName: string) => {
    try {
      const response = await api.get(`/as/v1/admin/nodegroup/status/${nodeGroupName}`, {
        headers: {
          'accept': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      if (isDevelopmentEnv()) console.error('Error fetching node group status:', error);
      throw error;
    }
  },

  // Autoscale store node-state (used for Nodes panel with metrics and debug actions)
  getAutoscaleNodeState: async () => {
    const response = await api.get('/as/v1/autoscale/store/node-state', {
      headers: { accept: 'application/json' },
    });
    return response.data;
  },
  deleteAutoscaleNodeState: async (key: string, nodeId: string) => {
    const encodedKey = encodeURIComponent(key);
    const response = await api.delete(`/as/v1/autoscale/store/node-state/${encodedKey}/${encodeURIComponent(nodeId)}`, {
      headers: { accept: 'application/json' },
    });
    return response.data;
  },
  forceAutoscaleNodeState: async (key: string, nodeId: string, state: string) => {
    const encodedKey = encodeURIComponent(key);
    const path = `/as/v1/autoscale/store/node-state/${encodedKey}/${encodeURIComponent(nodeId)}/force`;
    if (isDevelopmentEnv()) console.log('[forceAutoscaleNodeState] PUT request', { state, path, baseURL: api.defaults.baseURL, fullURL: `${api.defaults.baseURL}${path}` });
    const response = await api.put(path, JSON.stringify(state), {
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  },

  // Get specific node group - updated endpoint
  getNodeGroup: async (groupId: string) => {
    const response = await api.get(`/as/v1/admin/nodegroup/${groupId}`, {
      headers: {
        'accept': 'application/json'
      }
    });
    return response.data;
  },

  // Create node group - updated endpoint with longer timeout
  createNodeGroup: async (config: NodeGroupCreationConfig): Promise<any> => {
    try {
      // Use the configured axios instance which goes through the proxy
      const response = await api.post('/as/v1/admin/nodegroup', config, {
        headers: {
          'accept': 'application/json'
        },
        timeout: 60000 // 60 seconds timeout for node group creation
      });
      return response.data;
    } catch (error: any) {
      if (isDevelopmentEnv()) console.error('Create node group error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        code: error.code
      });
      
      // Handle timeout specifically
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        throw new Error('Node group creation is taking longer than expected. This is normal for cloud resource provisioning. Please check the Stream Manager logs or try again in a few minutes.');
      }
      
      // Provide more detailed error message
      if (error.response?.data) {
        throw new Error(`Server error: ${error.response.status} - ${JSON.stringify(error.response.data)}`);
      } else {
        throw new Error(`Request failed: ${error.message}`);
      }
    }
  },

  // Update node group - updated endpoint
  updateNodeGroup: async (nodeGroupData: any) => {
    const response = await api.put('/as/v1/admin/nodegroup', nodeGroupData, {
      headers: {
        'accept': '*/*',
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  },

  // Delete node group - updated endpoint
  deleteNodeGroup: async (groupId: string) => {
    const response = await api.delete(`/as/v1/admin/nodegroup/${groupId}`, {
      headers: {
        'accept': 'application/json'
      }
    });
    return response.data;
  },

  // Get streams for a specific node group
  getNodeGroupStreams: async (nodeGroupName: string) => {
    try {
      const response = await api.get(`/as/v1/streams/stream/${nodeGroupName}?stats=true&aggregate=false&streams=true`, {
        headers: {
          'accept': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      if (isDevelopmentEnv()) console.error('Error fetching streams:', error);
      throw error;
    }
  },

  // Get live streams (aggregated) for a specific node group
  getNodeGroupLiveStreams: async (nodeGroupName: string) => {
    try {
      const response = await api.get(`/as/v1/streams/stream/${nodeGroupName}?stats=true&aggregate=true&streams=true`, {
        headers: {
          'accept': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      if (isDevelopmentEnv()) console.error('Error fetching live streams (aggregate):', error);
      throw error;
    }
  },

  // Get origin node for publishing (RTMP/RTSP) – returns array with serverAddress etc.
  getPublishOrigin: async (nodeGroupName: string, streamName: string) => {
    try {
      const response = await api.get(
        `/as/v1/streams/stream/${nodeGroupName}/publish/${streamName}`,
        { headers: { 'accept': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      if (isDevelopmentEnv()) console.error('Error getting publish origin:', error);
      throw error;
    }
  },

  // Get edge for subscribe (RTMP/RTSP) – returns raw array for dialog (curl, response JSON, endpoint)
  getSubscribeEdge: async (nodeGroupName: string, streamGuid: string) => {
    try {
      const response = await api.get(
        `/as/v1/streams/stream/${nodeGroupName}/subscribe/${streamGuid}?strict=false`,
        { headers: { 'accept': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      if (isDevelopmentEnv()) console.error('Error getting subscribe edge:', error);
      throw error;
    }
  },
};

// Cloud Info API
export const cloudInfoAPI = {
  getAvailableRegions: async () => {
    const response = await api.get('/as/v1/admin/terraform/region', {
      headers: { 'accept': 'application/json' }
    });
    return response.data;
  },
  getAvailableImages: async () => {
    const response = await api.get('/as/v1/admin/terraform/image', {
      headers: { 'accept': 'application/json' }
    });
    return response.data;
  },
  getAvailableInstanceTypes: async () => {
    const response = await api.get('/as/v1/admin/terraform/instanceType', {
      headers: { 'accept': 'application/json' }
    });
    return response.data;
  },
};

// SRT API
export const srtAPI = {
  // List all stream provisions for a node group (SRT and Zixi)
  listAllProvisions: async (nodeGroup: string) => {
    try {
      const response = await api.get(`/as/v1/streams/provision/${nodeGroup}`, {
        headers: { 'accept': 'application/json' }
      });
      return response.data;
    } catch (error) {
      if (isDevelopmentEnv()) console.error('Error listing provisions:', error);
      throw error;
    }
  },

  /** @deprecated Use listAllProvisions. Kept for compatibility. */
  listAllSrtProvisions: async (nodeGroup: string) => srtAPI.listAllProvisions(nodeGroup),

  // Create RTMP Pull provision (type rtmp) – pull remote RTMP stream
  postRtmpPullProvision: async (
    app: string,
    streamName: string,
    nodeGroup: string,
    properties: { provisionGuid: string; rtmpUri: string; persist?: string }
  ) => {
    const streamGuid = `${app}/${streamName}`;
    try {
      const provision = {
        provisionGuid: properties.provisionGuid,
        streams: [
          {
            streamGuid,
            abrLevel: 0,
            camParams: {
              properties: {
                type: 'rtmp',
                action: 'create',
                rtmpUri: properties.rtmpUri,
                ...(properties.persist != null && { persist: properties.persist }),
              },
            },
          },
        ],
      };
      const response = await api.post(`/as/v1/streams/provision/${nodeGroup}`, [provision], {
        headers: { 'accept': '*/*', 'Content-Type': 'application/json' },
      });
      return response.data;
    } catch (error) {
      if (isDevelopmentEnv()) console.error('Error creating RTMP Pull provision:', error);
      throw error;
    }
  },

  // Create RTMP Push provision (type rtmp-push) – push stream to remote RTMP
  postRtmpPushProvision: async (
    app: string,
    streamName: string,
    nodeGroup: string,
    properties: { provisionGuid: string; rtmpUri: string; immediate?: string; persist?: string; attempts?: string; delayS?: string }
  ) => {
    const streamGuid = `${app}/${streamName}`;
    try {
      const provision = {
        provisionGuid: properties.provisionGuid,
        streams: [
          {
            streamGuid,
            abrLevel: 0,
            camParams: {
              properties: {
                type: 'rtmp-push',
                action: 'create',
                rtmpUri: properties.rtmpUri,
                ...(properties.immediate != null && { immediate: properties.immediate }),
                ...(properties.persist != null && { persist: properties.persist }),
                ...(properties.attempts != null && { attempts: properties.attempts }),
                ...(properties.delayS != null && { delayS: properties.delayS }),
              },
            },
          },
        ],
      };
      const response = await api.post(`/as/v1/streams/provision/${nodeGroup}`, [provision], {
        headers: { 'accept': '*/*', 'Content-Type': 'application/json' },
      });
      return response.data;
    } catch (error) {
      if (isDevelopmentEnv()) console.error('Error creating RTMP Push provision:', error);
      throw error;
    }
  },

  // Create IP Cam provision (type ipcam) – pull from IP camera
  postIpcamProvision: async (
    app: string,
    streamName: string,
    nodeGroup: string,
    properties: { provisionGuid: string; remoteContextPath: string; remoteStreamName: string; host: string; port: string }
  ) => {
    const streamGuid = `${app}/${streamName}`;
    try {
      const provision = {
        provisionGuid: properties.provisionGuid,
        streams: [
          {
            streamGuid,
            abrLevel: 0,
            camParams: {
              properties: {
                type: 'ipcam',
                action: 'create',
                remoteContextPath: properties.remoteContextPath,
                remoteStreamName: properties.remoteStreamName,
                host: properties.host,
                port: properties.port,
              },
            },
          },
        ],
      };
      const response = await api.post(`/as/v1/streams/provision/${nodeGroup}`, [provision], {
        headers: { 'accept': '*/*', 'Content-Type': 'application/json' },
      });
      return response.data;
    } catch (error) {
      if (isDevelopmentEnv()) console.error('Error creating IP Cam provision:', error);
      throw error;
    }
  },

  // Create MPEG-TS Listener provision (type mpegts) – listen for MPEG-TS multicast/unicast
  postMpegtsProvision: async (
    app: string,
    streamName: string,
    nodeGroup: string,
    properties: { provisionGuid: string; cast: string; ip: string; port: string; audio: boolean; video: boolean }
  ) => {
    const streamGuid = `${app}/${streamName}`;
    const portNum = parseInt(properties.port, 10) || 5555;
    try {
      const provision = {
        provisionGuid: properties.provisionGuid,
        streams: [
          {
            streamGuid,
            abrLevel: 0,
            camParams: {
              properties: {
                type: 'mpegts',
                action: 'create',
                cast: properties.cast,
                ip: properties.ip,
                port: portNum,
                audio: properties.audio,
                video: properties.video,
              },
            },
          },
        ],
      };
      const response = await api.post(`/as/v1/streams/provision/${nodeGroup}`, [provision], {
        headers: { 'accept': '*/*', 'Content-Type': 'application/json' },
      });
      return response.data;
    } catch (error) {
      if (isDevelopmentEnv()) console.error('Error creating MPEG-TS provision:', error);
      throw error;
    }
  },

  // Create a new File restreamer provision (type file) – live stream from local files
  postFileProvision: async (
    app: string,
    streamName: string,
    nodeGroup: string,
    properties: { provisionGuid: string; files: string; loop: boolean; index: number }
  ) => {
    const streamGuid = `${app}/${streamName}`;
    try {
      const provision = {
        provisionGuid: properties.provisionGuid,
        streams: [
          {
            streamGuid,
            abrLevel: 0,
            camParams: {
              properties: {
                type: 'file',
                action: 'create',
                files: properties.files,
                loop: properties.loop,
                index: properties.index,
              },
            },
          },
        ],
      };
      const response = await api.post(`/as/v1/streams/provision/${nodeGroup}`, [provision], {
        headers: { 'accept': '*/*', 'Content-Type': 'application/json' },
      });
      return response.data;
    } catch (error) {
      if (isDevelopmentEnv()) console.error('Error creating File provision:', error);
      throw error;
    }
  },

  // Create a new Zixi Push provision (type zixi-push) – push stream to Zixi Broadcaster
  postZixiPushProvision: async (
    app: string,
    streamName: string,
    nodeGroup: string,
    properties: { host: string; streamId: string; latency: number; persist?: string }
  ) => {
    const streamGuid = `${app}/${streamName}`;
    const provisionGuid = properties.streamId?.trim() || streamName;
    try {
      const provision = {
        provisionGuid,
        streams: [
          {
            streamGuid,
            abrLevel: 0,
            camParams: {
              properties: {
                type: 'zixi-push',
                action: 'create',
                host: properties.host,
                streamId: properties.streamId,
                latency: properties.latency,
                ...(properties.persist != null && { persist: properties.persist }),
              },
            },
          },
        ],
      };
      const response = await api.post(`/as/v1/streams/provision/${nodeGroup}`, [provision], {
        headers: { 'accept': '*/*', 'Content-Type': 'application/json' },
      });
      return response.data;
    } catch (error) {
      if (isDevelopmentEnv()) console.error('Error creating Zixi Push provision:', error);
      throw error;
    }
  },

  // Create a new Zixi provision (POST provision) – Zixi Pull
  postZixiProvision: async (
    app: string,
    streamName: string,
    nodeGroup: string,
    properties: { zixiUri: string; latency: number; sessionId?: string; ignoreDTLSErrors?: string; dtlsOnly?: string; ciphers?: string; aesType?: string; persist?: string }
  ) => {
    const streamGuid = `${app}/${streamName}`;
    try {
      const provision = {
        provisionGuid: streamName,
        streams: [
          {
            streamGuid,
            abrLevel: 0,
            camParams: {
              properties: {
                type: 'zixi',
                action: 'create',
                zixiUri: properties.zixiUri,
                latency: properties.latency,
                sessionId: properties.sessionId ?? '',
                ignoreDTLSErrors: properties.ignoreDTLSErrors ?? 'true',
                dtlsOnly: properties.dtlsOnly ?? 'false',
                ciphers: properties.ciphers ?? '',
                aesType: properties.aesType ?? '0',
                persist: properties.persist ?? 'true',
              },
            },
          },
        ],
      };
      const response = await api.post(`/as/v1/streams/provision/${nodeGroup}`, [provision], {
        headers: { 'accept': '*/*', 'Content-Type': 'application/json' },
      });
      return response.data;
    } catch (error) {
      if (isDevelopmentEnv()) console.error('Error creating Zixi provision:', error);
      throw error;
    }
  },

  // Get publish target for restream (second call for Zixi: GET publish?restream=true)
  getPublishForRestream: async (streamGuid: string, nodeGroup: string) => {
    try {
      const response = await api.get(
        `/as/v1/streams/stream/${nodeGroup}/publish/${streamGuid}?restream=true`,
        { headers: { 'accept': 'application/json' } }
      );
      const data = response.data;
      const origin = Array.isArray(data) && data.length > 0 ? data[0] : data;
      return origin;
    } catch (error) {
      if (isDevelopmentEnv()) console.error('Error getting publish for restream:', error);
      throw error;
    }
  },

  // Create a new SRT Listener provision (type srt)
  postSrtProvision: async (
    app: string,
    streamName: string,
    port: string,
    nodeGroup: string,
    properties: any
  ) => {
    try {
      const provision = {
        provisionGuid: streamName,
        streams: [
          {
            streamGuid: `${app}/${streamName}`,
            abrLevel: 0,
            camParams: {
              properties: {
                type: 'srt',
                action: 'create',
                ...properties
              }
            }
          }
        ]
      };
      const response = await api.post(`/as/v1/streams/provision/${nodeGroup}`, [provision], {
        headers: { 'accept': 'application/json' }
      });
      return response.data;
    } catch (error) {
      if (isDevelopmentEnv()) console.error('Error creating SRT provision:', error);
      throw error;
    }
  },

  // Create SRT Caller provision (type srt-caller) – restream existing stream to SRT listener
  postSrtCallerProvision: async (
    app: string,
    streamName: string,
    nodeGroup: string,
    properties: {
      provisionGuid: string;
      ip: string;
      port: string;
      frameType?: number;
      role?: string;
      mux?: boolean;
      latency?: number;
      reorder?: number;
      overhead?: number;
      keyLength?: number;
      unlinkClock?: boolean;
      audio?: boolean;
      video?: boolean;
      persist?: string;
    }
  ) => {
    const streamGuid = `${app}/${streamName}`;
    try {
      const provision = {
        provisionGuid: properties.provisionGuid,
        streams: [
          {
            streamGuid,
            abrLevel: 0,
            camParams: {
              properties: {
                action: 'create',
                type: 'srt-caller',
                ip: properties.ip,
                port: properties.port,
                frameType: properties.frameType ?? 0,
                role: properties.role ?? 'sender',
                mux: properties.mux ?? true,
                latency: properties.latency ?? 100,
                reorder: properties.reorder ?? 16,
                overhead: properties.overhead ?? 100,
                keyLength: properties.keyLength ?? 0,
                unlinkClock: properties.unlinkClock ?? false,
                audio: properties.audio ?? true,
                video: properties.video ?? true,
                ...(properties.persist != null && { persist: properties.persist }),
              },
            },
          },
        ],
      };
      const response = await api.post(`/as/v1/streams/provision/${nodeGroup}`, [provision], {
        headers: { 'accept': '*/*', 'Content-Type': 'application/json' },
      });
      return response.data;
    } catch (error) {
      if (isDevelopmentEnv()) console.error('Error creating SRT Caller provision:', error);
      throw error;
    }
  },

  // Delete/Remove an SRT provision (returns { status, data } for UI to show real response)
  killStrProvision: async (provisionGuid: string, nodeGroup: string) => {
    try {
      const response = await api.delete(`/as/v1/streams/provision/${nodeGroup}/${provisionGuid}`, {
        headers: { 'accept': 'application/json' }
      });
      return { status: response.status, data: response.data };
    } catch (error) {
      if (isDevelopmentEnv()) console.error('Error deleting SRT provision:', error);
      throw error;
    }
  },

  // Get node for SRT publish
  getNodeForSrtPublish: async (streamGuid: string, nodeGroup: string) => {
    try {
      const response = await api.get(
        `/as/v1/streams/stream/${nodeGroup}/publish/${streamGuid}?restream=true&persist=true`,
        {
          headers: { 'accept': 'application/json' }
        }
      );
      const data = response.data;
      const origin = Array.isArray(data) && data.length > 0 ? data[0] : data;
      if (origin.streamGuid && origin.serverAddress) {
        return origin;
      } else if (origin.error) {
        throw new Error(origin.error);
      } else {
        throw new Error('No streamGuid or serverAddress found in response.');
      }
    } catch (error) {
      if (isDevelopmentEnv()) console.error('Error getting node for SRT publish:', error);
      throw error;
    }
  },

  // Get node for RTMP/RTSP subscribe (returns edge serverAddress for subscribe URL)
  getNodeForRTMPSubscribe: async (streamGuid: string, nodeGroup: string) => {
    try {
      const response = await api.get(
        `/as/v1/streams/stream/${nodeGroup}/subscribe/${streamGuid}?strict=false`,
        {
          headers: { 'accept': 'application/json' }
        }
      );
      const data = response.data;
      const edge = Array.isArray(data) && data.length > 0 ? data[0] : data;
      if (edge.streamGuid && edge.serverAddress) {
        return edge;
      } else if (edge.error) {
        throw new Error(edge.error);
      } else {
        throw new Error('No streamGuid or serverAddress found in response.');
      }
    } catch (error) {
      if (isDevelopmentEnv()) console.error('Error getting node for RTMP subscribe:', error);
      throw error;
    }
  },

  // Get active streams (filtered for SRT/restreamer)
  getActiveStreams: async (nodeGroup: string) => {
    try {
      const response = await api.get(`/as/v1/streams/stream/${nodeGroup}`, {
        headers: { 'accept': 'application/json' }
      });
      const data = response.data;
      // Filter for live streams from restreamer
      const activeStreams = data.filter((stream: any) => {
        const { state, streamSource } = stream;
        return (
          state?.toLowerCase() === 'live' &&
          streamSource?.toLowerCase() === 'restreamer'
        );
      });
      return activeStreams;
    } catch (error) {
      if (isDevelopmentEnv()) console.error('Error getting active streams:', error);
      throw error;
    }
  },
};

export default api; 